/**
 * Application Version Information
 * Follows Semantic Versioning (SemVer) 2.0.0
 * Format: MAJOR.MINOR.PATCH
 *
 * MAJOR: Breaking changes or major feature overhauls
 * MINOR: New features that are backward-compatible
 * PATCH: Bug fixes and minor improvements
 */

export interface VersionInfo {
  version: string;
  buildDate: string;
  environment: 'development' | 'staging' | 'production';
  releaseNotes?: string;
}

export interface ReleaseNote {
  version: string;
  releaseDate: string;
  type: 'major' | 'minor' | 'patch';
  summary: {
    overview: string;
    highlights: string[];
  };
  backend: {
    features?: string[];
    improvements?: string[];
    bugFixes?: string[];
  };
  frontend: {
    features?: string[];
    improvements?: string[];
    bugFixes?: string[];
  };
}

export const APP_VERSION: VersionInfo = {
  version: '1.16.0',
  buildDate: '2026-02-25',
  environment: 'development',
  releaseNotes: 'Create Order API Fix - Parcel Duplicate Handling for Excel Pre-Upload Scenario'
};

export const VERSION_HISTORY: ReleaseNote[] = [
  {
    version: '1.16.0',
    releaseDate: '2026-02-25',
    type: 'minor',
    summary: {
      overview: 'Critical fix for Create Order API failure when parcels already exist from Excel upload, enabling seamless coexistence of Excel pre-upload and Shopee API order creation workflows',
      highlights: [
        'Fixed Create Order API returning retcode -100006 (database error) when parcels already exist from Excel upload',
        'Implemented parcel upsert logic: existing parcels are linked to the new Shopee order instead of failing on duplicate PK',
        'Supports Shopee reroute scenario where same carton gets a new unique_id after cancel/re-create',
        'Added detailed logging for parcel creation vs linking counts per order'
      ]
    },
    backend: {
      features: [],
      improvements: [
        'Create Order endpoint now checks for existing parcels before insert using GetParcelByTrackingNumber',
        'Existing parcels (from Excel upload) are automatically re-linked to the new Shopee order via ShopeeOrderUniqueId update',
        'New parcels (not from Excel) are created with default values as before',
        'Per-order logging of created vs linked parcel counts for operational visibility'
      ],
      bugFixes: [
        'CRITICAL: Fixed Parcels_pkey duplicate key violation (SqlState 23505) when Create Order API is called after Excel upload',
        'Root cause: Parcels table uses Parcel_tracking_number as PRIMARY KEY, and Excel upload creates parcels with same tracking numbers that Shopee API later sends',
        'Fix: Replaced blind parcel insert with upsert pattern - check existence first, then create or link accordingly'
      ]
    },
    frontend: {
      features: [],
      improvements: [],
      bugFixes: []
    }
  },
  {
    version: '1.15.0',
    releaseDate: '2025-12-10',
    type: 'minor',
    summary: {
      overview: 'Forwarding Admin panel reorganization with label updates and four new charge management modules',
      highlights: [
        'Renamed module labels for clarity: Operational Charges → Forwarding Charges, Carriers → Carriers & Co-Loaders, Agent Pricing → Rates Pricing',
        'Added 4 new charge management modules: Customs Brokerage Fees, Warehouse Fees, Trucking Charges, Other Charges',
        'Simplified Port Charges Management modal - hidden Carriers and Port Assignments tabs',
        'Improved UI organization for freight forwarding quotation workflow'
      ]
    },
    backend: {
      features: [],
      improvements: [],
      bugFixes: []
    },
    frontend: {
      features: [
        'Customs Brokerage Fees module: New admin panel card for customs clearance and brokerage fee management',
        'Warehouse Fees module: New admin panel card for warehouse storage and handling charges',
        'Trucking Charges module: New admin panel card for inland transportation and delivery fees',
        'Other Charges module: New admin panel card for miscellaneous charges and fees'
      ],
      improvements: [
        'Renamed "Operational Charges" to "Forwarding Charges" for clarity',
        'Renamed "Carriers" to "Carriers & Co-Loaders" to better represent scope',
        'Renamed "Agent Pricing" to "Rates Pricing" for consistency',
        'Simplified Port Charges Management modal - now shows only Port Charges tab',
        'Hidden Carriers tab from Port Charges modal (moved to dedicated Carriers module)',
        'Hidden Port Assignments tab from Port Charges modal (workflow simplification)'
      ],
      bugFixes: []
    }
  },
  {
    version: '1.14.0',
    releaseDate: '2025-12-05',
    type: 'minor',
    summary: {
      overview: 'Complete environment configuration separation for local development, VPS dev, and VPS production with critical Admin access fixes',
      highlights: [
        'Three-tier environment separation: Local (localhost), VPS Dev (dev.app.atr-rarr.ph), VPS Prod (live.app.atr-rarr.ph)',
        'Fixed Admin role authorization - Admin users can now access Excel import and all features',
        'Fixed VPS dev login issues - database password and JWT configuration corrected',
        'Created environment.development.ts for proper VPS dev frontend builds'
      ]
    },
    backend: {
      features: [
        'Environment Configuration Matrix: Separate appsettings files for each environment',
        'Local Development: appsettings.json with localhost:5186 and ShopeeTest database',
        'VPS Development: appsettings.Development.json with dev.app.atr-rarr.ph and rarrdb_dev',
        'VPS Production: appsettings.Production.json with live.app.atr-rarr.ph and rarrdb_prod'
      ],
      improvements: [
        'Clean environment separation prevents cross-environment database access',
        'JWT settings properly scoped per environment (Issuer/Audience)',
        'Database credentials secured per environment',
        'Enhanced logging retained for VPS dev debugging'
      ],
      bugFixes: [
        'CRITICAL: Fixed VPS dev database password (rarrpass123 → correct production password)',
        'CRITICAL: Restored VPS dev appsettings.Development.json (was stripped to logging-only)',
        'Fixed ExcelImportController authorization to accept both "Admin" and "admin" roles',
        'Fixed local backend using VPS config (now uses appsettings.json for localhost)'
      ]
    },
    frontend: {
      features: [
        'Created environment.development.ts for VPS dev builds (points to dev.app.atr-rarr.ph/api)',
        'Updated environment.ts for local development (points to localhost:5186/api)',
        'Angular build configuration with fileReplacements for development builds',
        'Three-tier environment build system: npm start (local), --configuration=development (VPS dev), --configuration=production (VPS prod)'
      ],
      improvements: [
        'Build automation ensures correct API endpoints per environment',
        'Local developers can work offline with local backend',
        'VPS dev environment fully functional for UAT testing',
        'GitHub Actions workflows deploy correct configurations automatically'
      ],
      bugFixes: [
        'CRITICAL: Fixed Admin users unable to see "Import Excel" button',
        'Removed Admin from isShopeeChecker$ observable in auth.service.ts',
        'Fixed VPS dev frontend calling localhost instead of dev.app.atr-rarr.ph',
        'Fixed version chronology (swapped v1.12.1 and v1.13.0 dates for accuracy)'
      ]
    }
  },
  {
    version: '1.13.0',
    releaseDate: '2025-12-05',
    type: 'minor',
    summary: {
      overview: 'Pre-Alert modal UX fix and duplicate check prevention for manual Excel uploads',
      highlights: [
        'Fixed modal closing UX bug - prevents accidental closure when clicking inside and releasing outside',
        'Added duplicate pre-alert check before upload to prevent overwriting API-created pre-alerts',
        'Improved data integrity for Shopee API integration workflow'
      ]
    },
    backend: {
      features: [],
      improvements: [],
      bugFixes: []
    },
    frontend: {
      features: [
        'Duplicate Check: API call checks if pre-alert exists before allowing upload',
        'Error Guidance: Clear message explains duplicate detection and provides user guidance',
        'Fail-Open Design: Upload continues if duplicate check fails (network error)'
      ],
      improvements: [
        'Modal Click Logic: Changed from click to mousedown event for precise backdrop detection',
        'Event Propagation: Added stopPropagation to modal content to prevent accidental closure',
        'User Experience: Form data preserved when clicking and dragging inside modal'
      ],
      bugFixes: [
        'Fixed modal closing when user clicks inside modal and releases mouse outside',
        'Fixed missing duplicate check allowing manual uploads to overwrite API-created pre-alerts',
        'Added PreAlertService.checkPreAlertExists() method with error handling'
      ]
    }
  },
  {
    version: '1.12.1',
    releaseDate: '2025-12-03',
    type: 'patch',
    summary: {
      overview: 'Carton auto-link feature for Shopee API integration and frontend UX improvements for pre-alert workflow',
      highlights: [
        'Auto-link existing cartons from Shopee API to manually uploaded pre-alerts',
        'Fixed "Extra Cartons" issue in mobile scanner caused by incomplete API data',
        'Duplicate pre-alert check prevents overwriting API-created pre-alerts',
        'Modal UX improvements prevent accidental closure during data entry'
      ]
    },
    backend: {
      features: [
        'Carton Auto-Link Logic: Automatically links existing cartons (from Shopee API) to pre-alerts during Excel upload',
        'ImportStatistics Class: Tracks created, linked, duplicate, and error counts separately',
        'Enhanced Logging: AUTOLINK prefix for easy filtering and debugging',
        'Idempotent Mapping: Won\'t create duplicate PreAlertCartonMapping entries on re-upload'
      ],
      improvements: [
        'ExcelImportService.cs (lines 702-750): Auto-link logic checks for existing cartons and creates mappings',
        'Statistics Tracking (lines 619-622, 686-689): Separate counters for linked vs created cartons',
        'API Response (lines 131-171): Returns detailed import statistics with linked count',
        'Batch Logging (lines 932-939): Reports created, linked, duplicates per batch'
      ],
      bugFixes: [
        'CRITICAL: Fixed Shopee API incomplete data issue - cartons now linked to pre-alerts instead of skipped',
        'Fixed mobile scanner "Extra Cartons" false positives caused by unlinked cartons',
        'Fixed Excel import skipping existing cartons without creating pre-alert mappings'
      ]
    },
    frontend: {
      features: [],
      improvements: [],
      bugFixes: []
    }
  },
  {
    version: '1.12.0',
    releaseDate: '2025-11-25',
    type: 'minor',
    summary: {
      overview: 'Dev/Prod environment separation with dual database infrastructure and automated deployment workflows',
      highlights: [
        'Separate production (live.app.atr-rarr.ph) and development (dev.app.atr-rarr.ph) environments',
        'Isolated databases (rarrdb_prod and rarrdb_dev) for safe testing',
        'Environment-specific configurations for frontend and backend',
        'GitHub Actions workflows for automated deployments',
        'Complete VPS infrastructure with systemd services and nginx reverse proxy'
      ]
    },
    backend: {
      features: [
        'appsettings.Production.json: Production config with rarrdb_prod database',
        'appsettings.Development.json: Development config with rarrdb_dev database',
        'Environment-specific JWT settings (Issuer/Audience per domain)',
        'CORS configuration supporting cross-environment testing',
        'VPS systemd services: rarr-backend-prod (port 5002), rarr-backend-dev (port 5001)',
        'PostgreSQL database cloning: rarrdb → rarrdb_prod + rarrdb_dev (604MB each)'
      ],
      improvements: [
        'Database isolation prevents test data from affecting production',
        'Separate backend processes enable independent scaling',
        'Environment-specific configurations improve security'
      ],
      bugFixes: []
    },
    frontend: {
      features: [
        'environment.ts: Development config pointing to dev.app.atr-rarr.ph/api',
        'environment.prod.ts: Production config pointing to live.app.atr-rarr.ph/api',
        'GitHub Actions: deploy-production.yml for production branch',
        'GitHub Actions: deploy-development.yml for develop branch',
        'Automated build and deployment workflows via SCP'
      ],
      improvements: [
        'Separate build configurations for dev vs prod',
        'Environment-specific API endpoints',
        'CORS support for cross-environment testing',
        'Manual deployment via WinSCP as fallback'
      ],
      bugFixes: [
        'Fixed Angular build defaulting to production config',
        'Fixed CORS errors with proper environment-specific origins',
        'Fixed nginx permission issues with parent directory execute permissions'
      ]
    }
  },
  {
    version: '1.11.1',
    releaseDate: '2025-11-25',
    type: 'patch',
    summary: {
      overview: 'PC Charge Code terminology correction in operational charges management',
      highlights: [
        'Fixed charge code dropdown label from "Processing Charges" to "Port Charges"',
        'Database migration script for data consistency',
        'Schema verification across all environments'
      ]
    },
    backend: {
      bugFixes: [
        'Created fix-pc-charge-code-name.sql migration script',
        'Safe update query with conditional WHERE clause',
        'Verified public schema usage across local and VPS'
      ]
    },
    frontend: {
      bugFixes: [
        'Updated operational-charges-modal.html line 238',
        'Changed "PC - Processing Charges" → "PC - Port Charges"',
        'Improved charge code clarity for users'
      ]
    }
  },
  {
    version: '1.11.0',
    releaseDate: '2025-11-24',
    type: 'minor',
    summary: {
      overview: 'Exchange Rate daily reminder system for quotation workflow with port charge modal fixes.',
      highlights: [
        'Exchange Rate Reminder: Daily USD/PHP rate prompt when creating quotations',
        'Exchange Rate API: Backend endpoints for rate management and status checking',
        'Port Charge Modal Fix: Agent and port dropdowns now load from actual database',
        'User Manual: Comprehensive Forwarding System documentation created'
      ]
    },
    backend: {
      features: [
        'Exchange Rate API: GET /status, GET /current, GET /history, POST endpoints',
        'ExchangeRate Entity: Database model with daily rate tracking',
        'ExchangeRateStatusDto: Status check with NeedsUpdate flag for daily reminders',
        'EF Migration: AddExchangeRatesTable creates forwarding.exchange_rates table'
      ],
      improvements: [
        'RemoveTimestampDefaults Migration: Fixed with conditional SQL for missing columns'
      ],
      bugFixes: []
    },
    frontend: {
      features: [
        'Exchange Rate Service: API communication for rate management',
        'Exchange Rate Modal: Auto-shows when rate not set for today',
        'Rate Indicator Button: Header shows "1 USD = XX.XX PHP" with click-to-update',
        'Historical Rates Reference: View and use last 14 days of rates',
        'User Manual: Complete FORWARDING-USER-MANUAL.md documentation'
      ],
      improvements: [
        'New Quotation Integration: Rate check on page init with auto-modal',
        'Port Charge Modal: Agent dropdown loads from AgentService',
        'Port Charge Modal: Port dropdown loads from PortService'
      ],
      bugFixes: [
        'Add Port Charge Modal: Fixed hardcoded agent/port dropdowns to use actual data'
      ]
    }
  },
  {
    version: '1.10.1',
    releaseDate: '2025-11-18',
    type: 'patch',
    summary: {
      overview: 'Quotation wizard UX improvements, charge filtering fixes, and ForwardingAdmin authorization.',
      highlights: [
        'Template Filter Fix: Templates now properly visible in wizard',
        'Container Types: Dynamic loading from database instead of hardcoded',
        'Charge Deduplication: Charges appear only once across wizard steps',
        'ForwardingAdmin Auth: Added role to all 13 Forwarding controllers'
      ]
    },
    backend: {
      features: [],
      improvements: [
        'ForwardingAdmin Role: Added to Authorize attributes on 38 endpoints across 13 controllers'
      ],
      bugFixes: [
        'QuotationController: Fixed property name mismatches (ContainerType vs ContainerLoadType)',
        'QuotationDtos: Removed duplicate class definitions causing compilation errors'
      ]
    },
    frontend: {
      features: [],
      improvements: [
        'Template Loading: Changed from double filter (isActive && status) to single filter (isActive)',
        'Container Types: Dynamic loading via ContainerLoadTypesService instead of hardcoded array',
        'Workflow Flexibility: Changed blocking validation to informational notice',
        'Step Titles: Use array index for step numbers instead of hardcoded values'
      ],
      bugFixes: [
        'Client/Agent Display: Fixed getClientName/getAgentName string/number conversion',
        'Charge Duplication: Added assignedChargeIds Set to prevent charges in multiple steps',
        'Step Numbering: Fixed "Step 1, 3, 5, 6" showing instead of "Step 1, 2, 3, 4"'
      ]
    }
  },
  {
    version: '1.10.0',
    releaseDate: '2025-11-12',
    type: 'minor',
    summary: {
      overview: 'Customs Clearance Progress update feature from Carton detail view with parcel selection modal and responsive button layout.',
      highlights: [
        'Carton-to-Customs Clearance: Direct navigation from carton detail to customs clearance progress',
        'Parcel Selection Modal: Bootstrap modal with checkbox list, filter, and select-all functionality',
        'Pre-population: Selected parcels automatically populate customs clearance form',
        'Responsive Buttons: Mobile-friendly button layout with proper spacing and wrapping'
      ]
    },
    backend: {
      features: [],
      improvements: [],
      bugFixes: []
    },
    frontend: {
      features: [
        'Update CustClearance Button: Added to carton detail view beside Repack Cancel',
        'Parcel Selection Modal: Bootstrap 5 native modal with filter input and checkbox list',
        'Filter Functionality: Real-time search/filter of parcel tracking numbers',
        'Select All: Bulk selection/deselection of filtered parcels',
        'Pre-population: Customs clearance form auto-fills with selected parcel tracking numbers',
        'Router State: Navigation passes selected parcels via Angular router state'
      ],
      improvements: [
        'Responsive Button Layout: flex-wrap with gap-2 for proper spacing on all screen sizes',
        'Mobile-Optimized: Button text abbreviation on mobile (hides "Repack"/"Update" prefix)',
        'Button Icons: Added Bootstrap icons to all action buttons for better UX',
        'Card Header Responsive: Flex-column on mobile, flex-row on tablet/desktop',
        'TypeScript Safety: Fixed null safety with ng-container and as syntax'
      ],
      bugFixes: []
    }
  },
  {
    version: '1.9.0',
    releaseDate: '2025-11-11',
    type: 'minor',
    summary: {
      overview: 'Role-Based Access Control for Forwarding module with two-tier permission system and customs clearance event code fixes.',
      highlights: [
        'Forwarding RBAC: Two new roles (ForwardingAdmin, ForwardingUser) with hierarchical permissions',
        'Route Guards: Dedicated authorization guards for forwarding routes',
        'Reactive Authorization: Observable-based role checking with BehaviorSubject pattern',
        'Bug Fix: Corrected customs clearance event codes (I01, I07) for proper format'
      ]
    },
    backend: {
      features: [],
      improvements: [],
      bugFixes: []
    },
    frontend: {
      features: [
        'ForwardingAdmin Role: Full access to all forwarding features including Admin Panel',
        'ForwardingUser Role: Limited access to Dashboard, Inquiries, and Quotes only',
        'Route Guards: forwardingAdminGuard and forwardingUserGuard with automatic redirection',
        'Reactive Observables: isForwardingAdmin$ and isForwardingUser$ for real-time role checking',
        'Conditional Navigation: Dashboard menu items appear/disappear based on user role',
        'Security Models: Extended LoginResponse interface to include new role types'
      ],
      improvements: [
        'AuthService: Added synchronous role check methods (isForwardingAdmin(), isForwardingUser())',
        'Dashboard Component: Integrated new role observables for reactive UI updates',
        'Route Configuration: Applied hierarchical guards to forwarding child routes',
        'Access Control Matrix: Clear documentation of role permissions'
      ],
      bugFixes: [
        'CRITICAL: Fixed customs clearance event codes - changed "01" to "I01" and "07" to "I07" for proper format',
        'Event Code Dropdown: Updated options to match correct event code standards',
        'Instructions Panel: Corrected event code documentation'
      ]
    }
  },
  {
    version: '1.8.0',
    releaseDate: '2025-11-04',
    type: 'minor',
    summary: {
      overview: 'Complete quotation creation workflow with template selection, dynamic charge management, and enhanced template editor.',
      highlights: [
        'Quotation Creation Wizard: 4-phase workflow from template selection to final quote',
        'Enhanced Template Editor: Full-featured modal with 3-tab interface',
        'Dynamic Charge Steps: Auto-generated forms based on template configuration',
        'Real-time Calculations: Automatic quote totals and charge summaries'
      ]
    },
    backend: {
      features: [],
      improvements: [],
      bugFixes: []
    },
    frontend: {
      features: [
        'New Quotation Component: Complete 4-phase wizard (~1,400 lines)',
        'Phase 1: Template selection with card grid UI and mode filtering',
        'Phase 2: Basic information form (client, origin, destination, incoterm, dates)',
        'Phase 3: Dynamic charge steps organized by 6 categories (Origin, Ocean, Destination, Insurance, Warehouse, Customs)',
        'Phase 4: Review & submit screen with quote summary and total calculations',
        'Edit Quote Template Modal: Full-featured editor (~1,200 lines)',
        'Template Settings Tab: Name, mode, incoterm scope, container type, status',
        'Charges Selection Tab: Real-time filtering by mode with grouped display',
        'HTML Template Tab: Rich text editor with preview functionality',
        'Quotation Service: API integration with quote calculation logic',
        'Quotation Models: Complete TypeScript interfaces for quotations and charges'
      ],
      improvements: [
        'Quote Templates Modal: Simplified to list view, edit delegated to dedicated modal',
        'Ports Modal: Enhanced CRUD operations and validation',
        'Container Load Types Service: Added graceful fallback to mock data on API failure',
        'Incoterm Service: Added graceful fallback to mock data on API failure',
        'Forwarding Admin: Integrated quotation wizard with seamless navigation'
      ],
      bugFixes: []
    }
  },
  {
    version: '1.7.0',
    releaseDate: '2025-10-28',
    type: 'minor',
    summary: {
      overview: 'Agent, Client, and Incoterm management systems for freight forwarding quotations with critical database compatibility fixes.',
      highlights: [
        'Agent Management: Full CRUD operations for freight forwarding agents',
        'Client Management: Complete client data with currency and markup settings',
        'Incoterm Management: Incoterms 2020 standard terms (11 codes)',
        'CRITICAL: Fixed PostgreSQL array type compatibility issues'
      ]
    },
    backend: {
      features: [
        'Agent API with soft delete support (/api/forwarding/agents)',
        'Client API with default currency and markup (/api/forwarding/clients)',
        'Incoterm API with 11 Incoterms 2020 entries (/api/forwarding/incoterms)',
        'JWT role-based authorization (Admin, ForwardingManager)',
        'Repository pattern with interface-based CRUD'
      ],
      bugFixes: [
        'CRITICAL: Fixed PostgreSQL text[] array type incompatibility in CarrierChargeTypes',
        'Converted applies_to column from text[] to VARCHAR(200)',
        'Custom migration with safe IF EXISTS checks',
        'Created missing forwarding.incoterms table'
      ]
    },
    frontend: {
      features: [
        'Agent Management modal with search and filtering',
        'Client Management modal with currency/markup configuration',
        'Incoterm Management modal with full CRUD operations',
        'Reactive services using BehaviorSubject pattern',
        'Bootstrap 5.3.3 modal implementation with z-index layering'
      ],
      bugFixes: [
        'Fixed modal components not appearing (missing ViewChild decorators)',
        'Added component imports to forwarding-admin module',
        'Fixed modal template integration'
      ]
    }
  },
  {
    version: '1.6.0',
    releaseDate: '2025-10-27',
    type: 'minor',
    summary: {
      overview: 'Port Charges Management System with complete 3-modal system for freight forwarding.',
      highlights: [
        'Ports Management: CRUD operations for port master data',
        'Carriers Management: 3-tab system (Carriers, Charge Types, Surcharges)',
        'Port Charges Management: 3-tab system (Port Charges, Port Assignments, History)'
      ]
    },
    backend: {
      features: [
        'Charge basis data structure with transport mode support',
        'Complete charge basis management including search and statistics',
        'Operational charges linked to charge basis units',
        'Pre-populated with 14 standard charge basis units',
        'Database ready with 20 common operational charges'
      ],
      improvements: [
        'Enhanced security with multi-role access control',
        'Database optimized for better performance',
        'Charge basis pre-configured for immediate use',
        'Operational charges ready for transport mode filtering'
      ]
    },
    frontend: {
      features: [
        'Charge basis statistics dashboard by transport mode',
        'Operational charges with smart dropdown selection',
        'Easy-to-use forms for charge basis management',
        'Real-time data updates from server'
      ],
      improvements: [
        'Faster loading with intelligent data caching',
        'Fixed modal scrolling for better usability',
        'Improved visual layering for multiple modals open',
        'Better organization with clear modal hierarchy'
      ]
    }
  },
  {
    version: '1.5.0',
    releaseDate: '2025-10-21',
    type: 'minor',
    summary: {
      overview: 'System Health Monitoring with real-time application health dashboard.',
      highlights: [
        'Real-time application health dashboard',
        'Performance metrics and API response time tracking',
        'Enhanced error logging infrastructure'
      ]
    },
    backend: {
      features: [
        'Remote error tracking system'
      ],
      bugFixes: [
        'Fixed batch update processing'
      ]
    },
    frontend: {
      features: [
        'Bulk update and copy to clipboard for mobile scanner',
        'Offline storage for scanned cartons',
        'Error log viewer with filtering',
        'Remote error tracking integration'
      ],
      improvements: [
        'Mobile scanner performance optimized',
        'Button alignment improved throughout'
      ],
      bugFixes: [
        'Resolved missing cartons in mobile scanner',
        'Fixed button alignment issues',
        'Fixed batch update functionality'
      ]
    }
  },
  {
    version: '1.4.0',
    releaseDate: '2025-10-16',
    type: 'minor',
    summary: {
      overview: 'Mobile Scanner UI improvements with button alignment consistency.',
      highlights: [
        'Fixed button alignment consistency across all scanner controls',
        'Removed arbitrary carton cap limit',
        'Users can now scan unlimited cartons per pre-alert'
      ]
    },
    backend: {
      improvements: [
        'Security updates for ShopeeChecker role',
        'Pre-alert access control updated'
      ]
    },
    frontend: {
      features: [
        'View-only pre-alert UI for ShopeeChecker role',
        'Scan status display in pre-alert lists',
        'Camera switch button for device flexibility',
        'OCR auto-completion with smart validation',
        'Enhanced tooltips and icons'
      ],
      bugFixes: [
        'CRITICAL: Fixed Android 15 camera black screen',
        'Removed problematic camera library',
        'Fixed past date validation issues',
        'Fixed API endpoint routing errors',
        'Standardized role naming'
      ]
    }
  },
  {
    version: '1.3.0',
    releaseDate: '2025-10-15',
    type: 'minor',
    summary: {
      overview: 'Batch Carton Update with bulk status update endpoint for mobile scanner.',
      highlights: [
        'Batch update endpoint for processing multiple cartons',
        'Transaction-based updates for data consistency',
        'Performance optimization for mobile scanner rendering'
      ]
    },
    backend: {
      features: [
        'Weekly accomplishment tracking system',
        'Automated progress report generation'
      ],
      bugFixes: [
        'API key generation improved for better compatibility',
        'Character set updated to exclude visually ambiguous characters',
        'Generated keys now use clean alphanumeric format',
        'Cryptographic security maintained with high entropy generation'
      ]
    },
    frontend: {
      features: [
        'Mobile scanner enhanced UX with duplicate prevention',
        'Slide-in animations for scan results',
        'Visual feedback for duplicate and new scans'
      ],
      improvements: [
        'Modern dark mode UI design',
        'Gradient progress bars and status indicators',
        'Celebration effects on completion',
        'Optimized for all mobile screen sizes',
        'Improved build performance'
      ]
    }
  },
  {
    version: '1.2.0',
    releaseDate: '2025-10-13',
    type: 'minor',
    summary: {
      overview: 'Offline Storage with persistent offline support for mobile scanner.',
      highlights: [
        'IndexedDB storage for scanned cartons',
        'Auto-sync when connection restored',
        'Error log viewer for remote debugging'
      ]
    },
    backend: {
      features: [
        'Real-time communication hub for scan progress',
        'Scan progress tracking with timestamps',
        'Database support for scan history',
        'Performance optimizations for real-time data'
      ],
      improvements: [
        'Async operations for better performance',
        'Group-based broadcasting for efficient updates',
        'Secure authentication for real-time connections',
        'Comprehensive logging for troubleshooting'
      ],
      bugFixes: [
        'Database field type updated to support larger encrypted values',
        'Fixed credential storage exceeding database field length limits',
        'Applied database migration for expanded credential storage'
      ]
    },
    frontend: {
      features: [
        'Real-time progress updates via live connection',
        'Progress card showing scan statistics',
        'Live connection status indicator',
        'Recent scans list with user information',
        'Animated progress bars',
        'Completion celebrations'
      ],
      improvements: [
        'Real-time communication library integrated',
        'Secure authentication for live updates',
        'Modern UI with Bootstrap icons',
        'Smooth animations throughout',
        'Proper cleanup to prevent memory issues',
        'Mobile-responsive design'
      ],
      bugFixes: [
        'Fixed form field binding issue'
      ]
    }
  },
  {
    version: '1.1.6',
    releaseDate: '2025-10-14',
    type: 'patch',
    summary: {
      overview: 'Mobile Scanner Carton Matching fixes - resolved 19 missing cartons issue.',
      highlights: [
        'Enhanced error logging in filter method',
        'Improved carton lookup logic',
        'Added deployment guide documentation'
      ]
    },
    backend: {
      improvements: [
        'Authentication management enhancements'
      ]
    },
    frontend: {
      features: [
        'Auto-detection of pre-alerts from scanned cartons',
        'Information panel showing pre-alert details',
        'Enhanced debugging tools'
      ],
      improvements: [
        'Comprehensive testing documentation',
        'Deployment guides updated'
      ]
    }
  },
  {
    version: '1.1.5',
    releaseDate: '2025-10-10',
    type: 'patch',
    summary: {
      overview: 'CRITICAL: ShopeeChecker role validation and view-only pre-alert UI.',
      highlights: [
        'Added ShopeeChecker to user DTO validation',
        'Database migration for role naming standardization',
        'View-only access to pre-alert data for ShopeeChecker role'
      ]
    },
    backend: {
      improvements: [
        'Client integration system enhancements'
      ]
    },
    frontend: {
      features: [
        'Admin bypass for customs clearance',
        'Improved access control system'
      ],
      improvements: [
        'UI styling improvements throughout',
        'Latest access control updates deployed'
      ],
      bugFixes: [
        'CRITICAL: Removed blocking issue for customs clearance access'
      ]
    }
  },
  {
    version: '1.1.4',
    releaseDate: '2025-10-08',
    type: 'patch',
    summary: {
      overview: 'Scan Status Display and Mobile Scanner Intelligence with OCR auto-completion.',
      highlights: [
        'Real-time carton scan status in pre-alert list',
        'CRITICAL: Fixed Android 15 camera black screen',
        'OCR auto-completion and smart validation'
      ]
    },
    backend: {
      features: [
        'Client integration returning complete data'
      ],
      bugFixes: [
        'CRITICAL: Excel upload functionality restored',
        'Fixed file upload processing',
        'Enhanced error reporting',
        'Fixed client integration data retrieval'
      ]
    },
    frontend: {
      features: [
        'Dark mode across all components',
        'Comprehensive error handling for Excel upload',
        'Theme toggle in dashboard'
      ],
      improvements: [
        'Modern dark mode foundation',
        'Excel import reliability improved',
        'Local storage for theme persistence'
      ],
      bugFixes: [
        'Excel upload fixed by using proper FormData with multipart/form-data encoding',
        'Authentication interceptor fixed to properly add authorization headers',
        'Theme flicker on page load eliminated with early localStorage read'
      ]
    }
  },
  {
    version: '1.1.3',
    releaseDate: '2025-10-06',
    type: 'patch',
    summary: {
      overview: 'Mobile Scanner Carton Matching using carrier_tn field and pre-alert backlog upload.',
      highlights: [
        'Fixed carton lookup logic to match database field',
        'Added ScannedBy tracking for audit trail',
        'Removed past date validation for historical pre-alerts'
      ]
    },
    backend: {
      features: [
        'Mobile scanning system',
        'Real-time communication infrastructure'
      ]
    },
    frontend: {
      features: [
        'Excel upload with file validation and drag-and-drop',
        'Barcode scanner for carton tracking',
        'Mobile carton scanner with camera and OCR',
        'Real-time communication demos',
        'Offline capabilities with service worker',
        'Offline/online indicator',
        'Responsive demo showcasing adaptive design',
        'Responsive layout system',
        'Shopee Excel upload integration'
      ],
      improvements: [
        'Deployment automation scripts',
        'Optimized build size',
        'Code splitting for faster loading',
        'Removed unused dependencies'
      ]
    }
  },
  {
    version: '1.1.2',
    releaseDate: '2025-10-02',
    type: 'patch',
    summary: {
      overview: 'Duplicate Request Handling with soft delete for Shopee API duplicate prevention.',
      highlights: [
        'JWT-based duplicate detection for UPDATE and PUSH_INFO endpoints',
        'Graceful handling without breaking existing data',
        'Soft delete implementation'
      ]
    },
    backend: {
      features: [
        'Health monitoring system',
        'Security monitoring'
      ]
    },
    frontend: {
      features: [
        'Security dashboard',
        'System health monitoring interface',
        'Automated deployment workflow'
      ],
      improvements: [
        'Production deployment automation',
        'Updated build tools',
        'Automatic frontend deployment'
      ]
    }
  },
  {
    version: '1.1.1',
    releaseDate: '2025-10-01',
    type: 'patch',
    summary: {
      overview: 'JWT Secret Generation improvements and Mobile Scanner UI Modernization.',
      highlights: [
        'Changed JWT secrets to alphanumeric format',
        'Mobile Scanner Discord-inspired design',
        'Enhanced UX with animations and visual feedback'
      ]
    },
    backend: {
      features: [
        'XML Template System'
      ]
    },
    frontend: {
      features: [
        'Enhanced manifest with XML template system',
        'Template management with interactive forms',
        'Integration management',
        'Shopee-specific dashboard',
        'Toast notification system',
        'User management interface'
      ],
      improvements: [
        'Navigation completely restructured',
        'Manifest form enhanced',
        'Template system debugged'
      ]
    }
  },
  {
    version: '1.1.0',
    releaseDate: '2025-09-30',
    type: 'minor',
    summary: {
      overview: 'Mobile Scanner Real-Time Progress with SignalR integration.',
      highlights: [
        'Real-time scan progress visibility for warehouse staff',
        'Multi-user collaboration support',
        'Fixed inbound credential save issues'
      ]
    },
    backend: {
      features: [
        'Customs clearance system',
        'Document service'
      ]
    },
    frontend: {
      features: [
        'Customs clearance interface',
        'Handover management',
        'Upgraded to latest framework'
      ],
      improvements: [
        'UI updates across system',
        'Framework modernization'
      ]
    }
  },
  {
    version: '1.0.3',
    releaseDate: '2025-09-26',
    type: 'patch',
    summary: {
      overview: 'JWT Authentication improvements supporting both string and object data formats.',
      highlights: [
        'Enhanced JWT validation flexibility',
        'Improved Shopee API configuration formatting',
        'Better compatibility with different data formats'
      ]
    },
    backend: {
      bugFixes: [
        'JWT authentication now supports both string and object formats',
        'Enhanced validation for Shopee API configuration'
      ]
    },
    frontend: {
      improvements: [
        'Better error handling for authentication'
      ]
    }
  },
  {
    version: '1.0.2',
    releaseDate: '2025-09-23',
    type: 'patch',
    summary: {
      overview: 'Mobile carton scanner auto-detection with smart pre-alert recognition.',
      highlights: [
        'Automatic pre-alert detection from carton scanning',
        'Pre-alert info panel with carton details',
        'Enhanced authentication debugging'
      ]
    },
    backend: {
      improvements: [
        'Authentication management enhancements'
      ]
    },
    frontend: {
      features: [
        'Auto-detection of pre-alerts from scanned cartons',
        'Information panel showing pre-alert details',
        'Enhanced debugging tools'
      ]
    }
  },
  {
    version: '1.0.1',
    releaseDate: '2025-09-16',
    type: 'patch',
    summary: {
      overview: 'Critical fixes for customs clearance access and client integration system.',
      highlights: [
        'Customs clearance access restored',
        'Fixed customs clearance progress access',
        'Client integration system fully functional'
      ]
    },
    backend: {
      improvements: [
        'Client integration system enhancements'
      ]
    },
    frontend: {
      features: [
        'Admin bypass for customs clearance',
        'Improved access control system'
      ],
      bugFixes: [
        'CRITICAL: Removed blocking issue for customs clearance access'
      ]
    }
  },
  {
    version: '1.0.0',
    releaseDate: '2025-09-09',
    type: 'major',
    summary: {
      overview: 'Initial production release - Excel Upload System fully operational with authentication and client integration.',
      highlights: [
        'Excel upload system for bulk pre-alert import',
        'JWT authentication with role-based access',
        'Client integration management',
        'Dark mode implementation across all components'
      ]
    },
    backend: {
      features: [
        'Container load type management for 20FT, 40FT, 40FT HC, 45FT containers, LCL and AIR shipments',
        'Carrier charge categories including documentation fees, handling fees, and terminal charges',
        'Route-based pricing system with origin, destination, and transport mode filtering',
        'Charge basis system with 14 standard units (Per KG, Per CBM, Per Container, etc.)',
        'Operational charges management with transport mode support',
        'Port management with full create, read, update, and delete capabilities',
        'Carrier management system with security controls',
        'Database structure for complete forwarding operations'
      ],
      improvements: [
        'Optimized database queries for faster response times',
        'Secure data deletion that preserves history',
        'Enhanced security with role-based access control',
        'Improved data validation on all forms',
        'Faster transport mode filtering',
        'Better system performance with async operations'
      ]
    },
    frontend: {
      features: [
        'Forwarding Admin Panel with 8 comprehensive management tools',
        'Container load types management interface',
        'Carrier charge types with search functionality',
        'Carrier management with 3 organized tabs',
        'Route-based pricing forms with dropdown selections',
        'Charge basis statistics dashboard',
        'Operational charges with dynamic dropdowns',
        'Port charges management with 3-tab organization',
        'Version history viewer with detailed release notes'
      ],
      improvements: [
        'Faster data loading with intelligent caching',
        'Smoother modal transitions with layered display',
        'Better visibility for nested modal windows',
        'Responsive design adapts to all screen sizes',
        'Instant updates without page refresh',
        'Modern, professional user interface',
        'Consistent spacing throughout the system'
      ],
      bugFixes: [
        'Fixed version button overlapping with modal windows',
        'Fixed version button visibility in sidebar',
        'Fixed charge basis dropdown not loading properly'
      ]
    }
  },
  {
    version: '0.4.0',
    releaseDate: '2025-07-04',
    type: 'minor',
    summary: {
      overview: 'Document tracking system and shipping progress features.',
      highlights: [
        'Complete document tracking',
        'Shipping progress monitoring',
        'Document lifecycle management'
      ]
    },
    backend: {
      features: [
        'Document tracking system',
        'Document service enhancements'
      ]
    },
    frontend: {
      features: [
        'Document tracking with real-time updates',
        'Shipping progress visualization',
        'Document lifecycle display'
      ],
      improvements: [
        'Shipping document UI updates',
        'Complete tracking implementation'
      ]
    }
  },
  {
    version: '0.3.0',
    releaseDate: '2025-06-27',
    type: 'minor',
    summary: {
      overview: 'Shipping Document features for air, land, and sea freight.',
      highlights: [
        'Document creation for all freight types',
        'Document detail and list views',
        'Comprehensive document management'
      ]
    },
    backend: {
      features: [
        'Document management system with full CRUD'
      ]
    },
    frontend: {
      features: [
        'Air and land document creation',
        'Sea freight document creation',
        'Document detail viewer',
        'Document list management'
      ],
      improvements: [
        'Shipping document features implemented',
        'UI/UX updates for documents'
      ]
    }
  },
  {
    version: '0.2.0',
    releaseDate: '2025-06-21',
    type: 'minor',
    summary: {
      overview: 'Complete repacking system for order fulfillment.',
      highlights: [
        'Repacking with multiple workflows',
        'Parcel management',
        'Optimized repacking transactions'
      ]
    },
    backend: {
      features: [
        'Repacking system with state management',
        'Parcel service'
      ]
    },
    frontend: {
      features: [
        'Add parcels to repack',
        'Add to existing repack',
        'Create new repack',
        'Update repack details'
      ],
      improvements: [
        'Repacking optimized',
        'Repacking transaction UI improved'
      ]
    }
  },
  {
    version: '0.1.0',
    releaseDate: '2025-05-28',
    type: 'minor',
    summary: {
      overview: 'Initial Shopee order management with real-time updates.',
      highlights: [
        'Order list with real-time updates',
        'Order details view',
        'E-commerce integration foundation'
      ]
    },
    backend: {
      features: [
        'Order management system',
        'Real-time communication setup',
        'Real-time order updates'
      ]
    },
    frontend: {
      features: [
        'Order list with live updates',
        'Order detail view',
        'Real-time status updates'
      ],
      improvements: [
        'Orders and order list updates',
        'Repack function updates'
      ]
    }
  },
  {
    version: '0.0.1',
    releaseDate: '2025-04-19',
    type: 'minor',
    summary: {
      overview: 'Initial project setup with authentication and user management foundation.',
      highlights: [
        'Project initialization with framework',
        'Authentication system foundation',
        'Basic user management'
      ]
    },
    backend: {
      features: [
        'User management with authentication',
        'Secure token-based system',
        'Database setup'
      ]
    },
    frontend: {
      features: [
        'Layout structure',
        'Login with authentication',
        'User forms',
        'User table display',
        'Error page'
      ],
      improvements: [
        'Initial project files',
        'Authentication setup',
        'User model configured',
        'API configuration'
      ]
    }
  }
];
